/**
 * THE PLANNING-SCOPE SWITCHER (PS-D12) and the scope's name beside a page title.
 *
 * BOTH RENDER NOTHING UNLESS THERE IS MORE THAN ONE SCOPE. A single-scope tenant (KCC) sees no
 * scope UI at all, admins included, so its shell is exactly what it was before scopes existed.
 *
 * The selection is the `scopeId` URL key. Choosing the default scope REMOVES the key (a default
 * URL stays clean), and any key that names a run or a page of the old scope's data (`runId`,
 * `compareWith`, `page`, `drawer`) is cleared with it: a run belongs to one scope.
 */
import { usePlanningScope } from "../api/queries/usePlanningScope";
import { useUrlState } from "../routes/urlState";

/** Route paths whose content is the selected scope's (a prefix match, except `/` and `/runs`). */
const EXACT = new Set(["/", "/runs"]);
const PREFIXES = [
  "/priority-policies",
  "/solver-configuration",
  "/analytics",
  "/reports",
  "/planning",
];

/** True for a page that shows the selected scope's data, where the scope's name belongs. */
export function isScopeKeyedPath(pathname: string): boolean {
  return (
    EXACT.has(pathname) || PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
  );
}

export function ScopeSwitcher({ className }: { className?: string }) {
  const { scopes, selected, multiScope } = usePlanningScope();
  const { push } = useUrlState(["scopeId", "runId", "compareWith", "page", "drawer"]);
  if (!multiScope || !selected) return null;

  return (
    <label className={`flex items-center gap-2xs ${className ?? ""}`}>
      <span className="type-caption text-muted-foreground">Scope</span>
      <select
        aria-label="Planning scope"
        value={selected.id}
        onChange={(event) => {
          const next = scopes.find((s) => s.id === event.currentTarget.value);
          push({
            scopeId: next && !next.isDefault ? next.id : null,
            runId: null,
            compareWith: null,
            page: null,
            drawer: null,
          });
        }}
        className="border-input bg-background type-caption h-[var(--control-height-md)] max-w-48 rounded-[var(--radius-sm)] border px-2"
      >
        {scopes.map((scope) => (
          <option key={scope.id} value={scope.id}>
            {scope.name}
          </option>
        ))}
      </select>
    </label>
  );
}

/** "· What if", beside a title on a scope-keyed page; nothing with one scope. */
export function ScopeName({ className }: { className?: string }) {
  const { selected, multiScope } = usePlanningScope();
  if (!multiScope || !selected) return null;
  return (
    <span className={`type-body text-muted-foreground ${className ?? ""}`} data-testid="scope-name">
      · {selected.name}
    </span>
  );
}
